import { type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

import type { AuthorityPath } from "@/shared/utils/authorityPath";
import type { EstateSize } from "@/shared/utils/estateSize";

const PATH_LABELS: Record<AuthorityPath | "ancillary", string> = {
  probate: "Probate",
  administration: "Administration",
  sea: "Small estate",
  ancillary: "Ancillary",
};

const CASE_TYPE_PATHS: Record<string, AuthorityPath | "ancillary"> = {
  probate: "probate",
  administration: "administration",
  small_estate: "sea",
  ancillary: "ancillary",
};

export interface Fiduciary {
  name: string;
  title: string;
  appointedOn: string;
}

interface SummaryTilesProps {
  firstName: string;
  caseType: string | null;
  authorityPath: AuthorityPath | null;
  fiduciary: Fiduciary | null;
  estateSize: EstateSize;
  state: string;
  onAddAddress: () => void;
}

interface TileProps {
  title: string;
  value?: string;
  empty: string;
  detail?: ReactNode;
}

const Tile: FC<TileProps> = ({ title, value, empty, detail }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg border border-primary bg-white p-4">
    <p className="text-sm text-secondary">{title}</p>
    <div className="flex flex-col gap-1">
      <p className={value ? "font-medium text-primary" : "text-tertiary"}>{value || empty}</p>
      {detail && <div className="text-[13px] text-secondary">{detail}</div>}
    </div>
  </div>
);

const TileLink: FC<{ to: string; children: ReactNode }> = ({ to, children }) => (
  <Link to={to} className="text-brand no-underline">
    {children}
  </Link>
);

export const SummaryTiles: FC<SummaryTilesProps> = ({
  firstName,
  caseType,
  authorityPath,
  fiduciary,
  estateSize,
  state,
  onAddAddress,
}) => {
  const path = (caseType && CASE_TYPE_PATHS[caseType]) || authorityPath;
  const reasons: Partial<Record<AuthorityPath, string>> = {
    probate: firstName ? `Based on ${firstName}'s will` : "Based on the will",
    administration: "No will",
    sea: "Under the small estate limit",
  };
  const propertyCount = estateSize.propertyCount;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <Tile
        title="Settlement path"
        value={path ? PATH_LABELS[path] : undefined}
        empty="Not known yet"
        detail={
          path ? (
            path !== "ancillary" && reasons[path]
          ) : (
            <TileLink to={POST_LOSS_ROUTES.GET_AUTHORIZED}>Complete triage</TileLink>
          )
        }
      />
      <Tile
        title={fiduciary?.title ?? "Executor"}
        value={fiduciary?.name}
        empty="Not appointed yet"
        detail={
          fiduciary ? (
            fiduciary.appointedOn && `Appointed ${fiduciary.appointedOn}`
          ) : (
            <TileLink to={POST_LOSS_ROUTES.GET_AUTHORIZED}>Get authorized</TileLink>
          )
        }
      />
      <Tile
        title="Estate size"
        value={estateSize.gross?.short}
        empty="No assets yet"
        detail={
          estateSize.gross ? (
            `Based on ${estateSize.valuedAssetCount} ${estateSize.valuedAssetCount === 1 ? "asset" : "assets"}`
          ) : (
            <TileLink to={POST_LOSS_ROUTES.ESTATE_INVENTORY}>Add assets to estimate</TileLink>
          )
        }
      />
      <Tile
        title="State"
        value={state}
        empty="Not known yet"
        detail={
          state ? (
            propertyCount > 0 &&
            `${propertyCount} ${propertyCount === 1 ? "property" : "properties"}`
          ) : (
            <button type="button" onClick={onAddAddress} className="text-brand">
              Add last address
            </button>
          )
        }
      />
    </div>
  );
};
